import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { refusalText } from "../../../api/errors";
import { useApiQuery } from "../../../api/hooks";
import type { DeliverableView, ReadLike, UploadPart } from "../../../api/types";
import { Button, Card, EmptyState, Loading, SheetIcon, showToast } from "../../../ui";
import { Check, FormError, Select, TextArea, TextInput } from "../components/fields";
import { useApiUpload, useCommandId, useDocumentKinds, useRefresh, type DocumentKindInfo } from "../hooks";
import { sizeWords } from "../lib/text";
import { READ_LIKE_ORDER, READ_LIKE_WORDS } from "../lib/words";
import "../item.css";

/** The files the app can read, as an `accept` list. */
export const UPLOAD_ACCEPT = ".pdf,.xlsx,.xls,.csv,.docx,.txt,.md,.xml,.png,.jpg,.jpeg,.tif,.tiff";

type PartRow = { documentKindId: string; pageRange: string; sheetRange: string };

/** What a form sends for the kinds of document in one file, or null when the file holds one kind. */
export function partsToSend(rows: readonly PartRow[]): UploadPart[] | null {
  if (rows.length < 2) return null;
  return rows.map((row) => ({
    document_kind_id: row.documentKindId,
    page_range: row.pageRange.trim() || null,
    sheet_range: row.sheetRange.trim() || null,
  }));
}

/** What the person said when they added the file, for a view that goes on from there. */
export interface UploadSent {
  fileName: string;
  /** "What I want from it", as written. */
  wanted: string;
}

export interface UploadFormProps {
  /** A file already chosen, for a form opened by dropping a file. The file box then shows its name, and a button changes it. */
  file?: File | null;
  /** The session the document goes to, when the form was opened from one. */
  sessionId?: string | null;
  /**
   * Finds or starts the session when the document is sent, for a view that keeps its sessions out of sight. It runs when the
   * person presses the button and not before, so closing the form starts nothing. A null answer sends the document with no session.
   */
  getSessionId?: () => Promise<string | null>;
  /** Called with the documents the file made, after the server took it. */
  onUploaded: (made: DeliverableView[], sent: UploadSent) => void;
  /** The way out: a Cancel link or button. */
  cancel: ReactNode;
  /** The page has its fields in cards; the dialog (a form opened over a view) has them plain. */
  variant?: "page" | "dialog";
}

/** One block of the form: a card on the page, a plain block in a dialog. */
function Block({ variant, children }: { variant: "page" | "dialog"; children: ReactNode }) {
  return variant === "dialog" ? <div className="stack">{children}</div> : <Card>{children}</Card>;
}

/**
 * The form that adds a document for the agent to read: the kind of document, what is wanted from it, and the file. It is the
 * body of the page at /deliverables/new, and the form the chat opens when a file is dropped on it. The kind is never guessed
 * from the file's name: the person picks it, and for a kind the team made itself they can say "let the app decide" and the
 * agent asks which kind it is (DEC-1103-15).
 */
export function UploadForm({ file: given = null, sessionId = null, getSessionId, onUploaded, cancel, variant = "page" }: UploadFormProps) {
  const refresh = useRefresh();
  const kinds = useDocumentKinds();
  const existing = useApiQuery("list_deliverables", {}, { staleTime: 10_000 });
  const upload = useApiUpload("upload_deliverable");
  const ids = useCommandId();
  const fileRef = useRef<HTMLInputElement>(null);

  const [kindId, setKindId] = useState("");
  const [readLike, setReadLike] = useState<ReadLike | "">("");
  const [wanted, setWanted] = useState("");
  const [file, setFile] = useState<File | null>(given);
  const [changingFile, setChangingFile] = useState(false);
  const [twoKinds, setTwoKinds] = useState(false);
  const [parts, setParts] = useState<PartRow[]>([
    { documentKindId: "", pageRange: "", sheetRange: "" },
    { documentKindId: "", pageRange: "", sheetRange: "" },
  ]);
  const [newerThan, setNewerThan] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [starting, setStarting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const chosen: DocumentKindInfo | undefined = kinds.kinds.find((kind) => kind.id === kindId);
  const teamsOwn = !!chosen && chosen.origin === "work";
  const kindOptions = kinds.kinds.map((kind) => ({ value: kind.id, label: kind.deliverableKind ? `${kind.name} (kind ${kind.deliverableKind})` : kind.name }));
  const list = existing.data ?? [];
  // A file that came in with the form is shown by name; the box to choose another opens on request.
  const showFileBox = !given || changingFile;

  const send = (chosenFile: File, session: string | null) => {
    // The promise, not the options of `mutate`, so the follow-up still runs when the form was closed while the file was on its way.
    upload
      .mutateAsync({
        file: chosenFile,
        input: {
          command_id: ids.current(),
          document_kind_id: twoKinds ? (parts[0]?.documentKindId ?? "") : kindId,
          what_i_want: wanted.trim(),
          read_like: teamsOwn && readLike ? readLike : null,
          newer_revision_of: newerThan || null,
          session_id: session,
          parts: twoKinds ? partsToSend(parts) : null,
        },
      })
      .then(
        (reply) => {
          ids.renew();
          refresh("list_deliverables", "deliverable", "item_backlog", "session", "home");
          const made = reply.data;
          if (made.length > 1) showToast({ text: `${made.length} documents were made from this file: one for each kind.` });
          onUploaded(made, { fileName: chosenFile.name, wanted: wanted.trim() });
        },
        () => {
          // A refusal shows under the button, through `upload.error`.
        },
      );
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (twoKinds) {
      if (parts.some((part) => !part.documentKindId)) found.parts = "Choose the kind of document for every part of the file.";
    } else if (!kindId) found.kind = "Choose the kind of document.";
    if (!wanted.trim()) found.wanted = "Say in a few words what you want from it.";
    if (!file) found.file = "Choose the file to add.";
    setErrors(found);
    setProblem(null);
    if (Object.keys(found).length > 0 || !file) return;

    if (!getSessionId) {
      send(file, sessionId);
      return;
    }
    setStarting(true);
    getSessionId().then(
      (session) => {
        setStarting(false);
        send(file, session);
      },
      (error: unknown) => {
        setStarting(false);
        setProblem(refusalText(error));
      },
    );
  };

  return (
    <form onSubmit={submit} noValidate className="stack" aria-label="Add a document">
      <Block variant={variant}>
        {kinds.loading && <Loading>Reading the kinds of document</Loading>}
        {!kinds.loading && kinds.kinds.length === 0 && (
          <EmptyState
            title="No kind of document is stored yet"
            action={
              <Link className="button button--secondary" to="/elements/new?kind=document_kind">
                Add a document kind
              </Link>
            }
          >
            The kind of document fixes the kind of deliverable and how the agent reads it.
          </EmptyState>
        )}
        {!twoKinds && (
          <Select label="Kind of document" required value={kindId} placeholder="Choose the kind of document" options={kindOptions} error={errors.kind} onChange={setKindId} />
        )}
        {chosen?.deliverableKind && (
          <p className="muted">
            <SheetIcon kind={chosen.deliverableKind} size={16} label={chosen.name} /> A deliverable of kind {chosen.deliverableKind}.
          </p>
        )}
        {!twoKinds && teamsOwn && (
          <Select
            label="Read it like"
            hint="This kind of document is your team's own. Say which of the usual types it reads like, so the agent knows how to read it."
            value={readLike}
            placeholder="Let the app decide"
            options={READ_LIKE_ORDER.map((like) => ({ value: like, label: READ_LIKE_WORDS[like] }))}
            onChange={(value) => setReadLike(value as ReadLike | "")}
          />
        )}
        <TextArea
          label="What I want from it"
          required
          rows={2}
          hint="The agent reads with this in mind."
          value={wanted}
          error={errors.wanted}
          onChange={setWanted}
        />
        <div className="field">
          {showFileBox ? (
            <>
              <label htmlFor="upload-file" className="field__label">
                The file <span className="field__required">(needed)</span>
              </label>
              <p className="field__hint muted">PDF, Excel, CSV, Word, text, a DEXPI file or a picture. Pictures and scans are read by the model.</p>
              <input
                id="upload-file"
                ref={fileRef}
                type="file"
                className="field__input"
                accept={UPLOAD_ACCEPT}
                aria-invalid={errors.file ? true : undefined}
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
            </>
          ) : (
            <>
              <span className="field__label">
                The file <span className="field__required">(needed)</span>
              </span>
              <p>
                <Button variant="quiet" onClick={() => setChangingFile(true)}>
                  Choose another file
                </Button>
              </p>
            </>
          )}
          {file && (
            <p className="muted">
              {file.name} {file.size ? `(${sizeWords(file.size)})` : ""}
            </p>
          )}
          {errors.file && (
            <p role="alert" className="field__error">
              {errors.file}
            </p>
          )}
        </div>
        <Check
          label="This file holds two kinds of document"
          hint="It is stored once and becomes two documents, each with its own pages or sheets."
          checked={twoKinds}
          onChange={setTwoKinds}
        />
        {twoKinds && (
          <fieldset className="field field--group">
            <legend className="field__label">The kinds of document in the file</legend>
            {errors.parts && (
              <p role="alert" className="field__error">
                {errors.parts}
              </p>
            )}
            {parts.map((part, index) => (
              <div key={index} className="row part-row">
                <Select
                  label={`Part ${index + 1}: kind of document`}
                  value={part.documentKindId}
                  placeholder="Choose the kind"
                  options={kindOptions}
                  onChange={(value) => setParts((rows) => rows.map((r, i) => (i === index ? { ...r, documentKindId: value } : r)))}
                />
                <TextInput
                  label={`Part ${index + 1}: pages`}
                  hint="For example 1-3"
                  value={part.pageRange}
                  onChange={(value) => setParts((rows) => rows.map((r, i) => (i === index ? { ...r, pageRange: value } : r)))}
                />
                <TextInput
                  label={`Part ${index + 1}: sheets`}
                  hint="Name the sheets"
                  value={part.sheetRange}
                  onChange={(value) => setParts((rows) => rows.map((r, i) => (i === index ? { ...r, sheetRange: value } : r)))}
                />
              </div>
            ))}
            <div>
              <Button variant="quiet" onClick={() => setParts((rows) => [...rows, { documentKindId: "", pageRange: "", sheetRange: "" }])}>
                Add another part
              </Button>
            </div>
          </fieldset>
        )}
        {list.length > 0 && (
          <Select
            label="This file is a newer revision of"
            hint="The spots already read from the older document are kept and marked out of date."
            value={newerThan}
            placeholder="It is not a newer revision"
            options={list.map((d) => ({ value: d.id, label: d.name }))}
            onChange={setNewerThan}
          />
        )}
      </Block>
      <Block variant={variant}>
        <div className="row">
          <Button type="submit" variant="primary" busy={upload.isPending || starting}>
            Add the document
          </Button>
          {cancel}
        </div>
        <FormError message={problem ?? (upload.error ? refusalText(upload.error) : null)} />
      </Block>
    </form>
  );
}
